import { ApiService } from '@cue/api';
import { CollectionViewer } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { NavigationResourceBasicInfo, NavigationResourceBasicInfoWithFirstLetter } from '@cue/data';
import { hasAppId, HasAppId, hasKioskId, HasKioskId } from '@cue/navigation-services';

export type ResourceListDataSourceRequest = {
  languageCode: string;
  searchString: string;
};

export type ResourceListDataSourceResponse = {
  total: number;
  data: NavigationResourceBasicInfo[];
  alphabetInfo: {
    letter: string;
    available: boolean;
    firstIndex: number;
    count: number;
  }[];
};

@Injectable({
  providedIn: 'root',
})
export class ResourceListDataSourceFactory {
  apiService = inject(ApiService);
  create(params: ResourceListDataSourceRequest & (HasAppId | HasKioskId)) {
    let payload = {
      ...params,
      skip: 0,
      take: 10,
    };

    if (hasAppId(params) && params.appId) {
      payload = {
        ...payload,
        appId: params.appId,
      };
    }

    return this.apiService
      .call<any, ResourceListDataSourceResponse>({
        url: '/api/FloorplanApp/list',
        method: 'post',
        payload,
      })
      .pipe(map((x) => new ResourceListDataSource(this.apiService, 20, x.total, params)));
  }
}

export class ResourceListDataSource {
  private _pageSize = 20;
  private _cachedData!: NavigationResourceBasicInfoWithFirstLetter[];
  private readonly _dataStream!: BehaviorSubject<(NavigationResourceBasicInfoWithFirstLetter | undefined)[]>;
  private readonly _subscription = new Subscription();

  private _fetchedPages = new Set<number>();

  connect(collectionViewer: CollectionViewer): Observable<(NavigationResourceBasicInfoWithFirstLetter | undefined)[]> {
    this._subscription.add(
      collectionViewer.viewChange.subscribe((range) => {
        const startPage = this._getPageForIndex(range.start);
        const endPage = this._getPageForIndex(range.end - 1);
        for (let i = startPage; i <= endPage; i++) {
          this._fetchPage(i);
        }
      })
    );
    return this._dataStream;
  }

  private _getPageForIndex(index: number): number {
    return Math.floor(index / this._pageSize);
  }

  disconnect(): void {
    this._subscription.unsubscribe();
  }

  private _fetchPage(page: number) {
    if (this._fetchedPages.has(page)) {
      return;
    }
    this._fetchedPages.add(page);

    // Use `setTimeout` to simulate fetching data from server.
    this.apiService
      .call<any, any>({
        url: '/api/navigation/basic-list-v2',
        method: 'post',
        payload: {
          ...this.params,
          skip: page * this._pageSize,
          take: this._pageSize,
        },
      })
      .pipe(
        map((x) => {
          return {
            ...x,
            data: x.data.map((a, index) => {
              if (index % 2 === 0) {
                return {
                  ...a,
                  subject: 'Testovaci event',
                  start: new Date(Date.now()),
                  end: new Date(Date.now() + 24),
                  organizer: 'Karel',
                };
              } else {
                return a;
              }
            }),
          };
        })
      )
      .subscribe((r: { data: NavigationResourceBasicInfoWithFirstLetter[] }) => {
        this._cachedData.splice(page * this._pageSize, this._pageSize, ...r.data);
        this._dataStream.next(this._cachedData);
      });
  }

  constructor(
    private apiService: ApiService,
    private pageSize: number,
    public total: number,
    private params: ResourceListDataSourceRequest & (HasAppId | HasKioskId)
  ) {
    this._cachedData = Array.from<NavigationResourceBasicInfoWithFirstLetter>({ length: this.total });
    this._dataStream = new BehaviorSubject<(NavigationResourceBasicInfoWithFirstLetter | undefined)[]>(
      this._cachedData
    );
  }
}
